/**
 * O hash de cada documento confrontado com o protocolo (E91.F2, GUS-51 RF04).
 *
 * O QA transversal de 2026-09-12 mediu (UXD-04) que a pergunta do banco — "o
 * hash confere?" — tinha resposta na tela, mas dividida com o grau e com um
 * "Verificado" que parecia aprovação. Aqui a resposta vira UM estado por
 * documento, lido do item `hash_ccb` que o motor gravou; a tela não compara
 * hash nenhum (mesma doutrina de `confrontoDaPeca`).
 *
 * Os seis estados, e de onde cada um sai:
 *
 * - `confere` / `divergente` / `sem_referencia`: o item `hash_ccb` do resultado
 *   gravado (`confere` / `nao_confere` / `ausente`).
 * - `nao_conferivel`: o item gravado `nao_executado`. O motor grava assim a
 *   integridade declarada na assinatura do arquivo original, que este arquivo
 *   (conversão posterior) não permite conferir (E97.F3, ADR-0082 §2.4); o
 *   documento impresso em imagem (E101.F5); e o código que só consta de tela
 *   impressa pelo banco depois do ato, que não é hash declarado (E132.F1,
 *   ADR-0086 §2.7) — o motivo vem no `detalhe` do item. O documento foi
 *   verificado: lê-lo como "ainda não verificado" punha "5 de 5 verificados"
 *   ao lado de "5 ainda não verificados" (smoke real da E97).
 * - `nao_verificado`: o documento não tem resultado — exame inexistente ou
 *   rascunho.
 * - `em_processamento`: o caso está sendo verificado E este documento ainda
 *   não tem resultado. Documento que JÁ tem resultado mantém o estado gravado
 *   enquanto processa: a E89.F2 (RF05) decidiu que o resultado anterior fica à
 *   vista, esmaecido e marcado, em vez de sumir — e este módulo não desfaz isso.
 *   Peça concluída nunca entra em processamento: a verificação a pula.
 */
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { confrontoDaPeca } from '@/lib/exame_eletronico_peca';

export const ESTADOS_DO_HASH = [
  'confere',
  'divergente',
  'sem_referencia',
  'nao_conferivel',
  'nao_verificado',
  'em_processamento',
] as const;

export type HashComOProtocolo = (typeof ESTADOS_DO_HASH)[number];

export function hashComOProtocolo(
  peca: ExameEletronicoCcbData,
  emProcessamento = false,
): HashComOProtocolo {
  const item = confrontoDaPeca(peca);
  if (item === 'confere') return 'confere';
  if (item === 'nao_confere') return 'divergente';
  if (item === 'ausente') return 'sem_referencia';
  if (item === 'nao_executado') return 'nao_conferivel';
  const concluida = peca.exame?.status === 'concluido';
  return emProcessamento && !concluida ? 'em_processamento' : 'nao_verificado';
}

export type ContagemDoConfronto = Record<HashComOProtocolo, number> & { total: number };

/** Quantos documentos há em cada estado — a matéria-prima do resumo. */
export function contagemDoConfronto(
  pecas: readonly ExameEletronicoCcbData[],
  emProcessamento = false,
): ContagemDoConfronto {
  const contagem: ContagemDoConfronto = {
    total: pecas.length,
    confere: 0,
    divergente: 0,
    sem_referencia: 0,
    nao_conferivel: 0,
    nao_verificado: 0,
    em_processamento: 0,
  };
  for (const peca of pecas) contagem[hashComOProtocolo(peca, emProcessamento)] += 1;
  return contagem;
}

/** Singular e plural de cada parte do resumo; `{n}` é a contagem no plural. */
export interface RotuloContado {
  um: string;
  varios: string;
}

export type RotulosDoResumo = Record<'documentos' | HashComOProtocolo, RotuloContado>;

function contado(rotulo: RotuloContado, n: number): string {
  return n === 1 ? rotulo.um : rotulo.varios.replace('{n}', String(n));
}

/**
 * A frase do confronto: '3 documentos · 2 hashes conferem · 1 sem referência'.
 *
 * Os rótulos entram por parâmetro para a função continuar pura (sem importar o
 * catálogo): o teste prende a composição, a tela prende as palavras. Só os
 * estados com contagem aparecem; o total vem sempre.
 */
export function resumoDoConfronto(contagem: ContagemDoConfronto, rotulos: RotulosDoResumo): string {
  const partes = [contado(rotulos.documentos, contagem.total)];
  for (const estado of ESTADOS_DO_HASH) {
    if (contagem[estado] > 0) partes.push(contado(rotulos[estado], contagem[estado]));
  }
  return partes.join(' · ');
}
